import { useEffect, useRef, useState, useCallback } from 'react';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutes of inactivity before logout
const WARNING_BEFORE_MS = 2 * 60 * 1000;  // Show warning 2 minutes before logout

const useSessionTimeout = ({ onLogout, enabled = true }) => {
    const [showWarning, setShowWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(120);
    const logoutTimerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const countdownRef = useRef(null);

    const clearAllTimers = useCallback(() => {
        clearTimeout(logoutTimerRef.current);
        clearTimeout(warningTimerRef.current);
        clearInterval(countdownRef.current);
    }, []);

    const startTimers = useCallback(() => {
        clearAllTimers();
        setShowWarning(false);

        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
            setSecondsLeft(WARNING_BEFORE_MS / 1000);
            countdownRef.current = setInterval(() => {
                setSecondsLeft(s => s - 1);
            }, 1000);
        }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

        logoutTimerRef.current = setTimeout(() => {
            clearAllTimers();
            setShowWarning(false);
            onLogout();
        }, IDLE_TIMEOUT_MS);
    }, [clearAllTimers, onLogout]);

    const resetTimer = useCallback(() => {
        if (!enabled) return;
        startTimers();
    }, [enabled, startTimers]);

    const stayLoggedIn = useCallback(() => {
        setShowWarning(false);
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!enabled) return;

        startTimers();

        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
        const handleActivity = () => {
            if (!showWarning) resetTimer();
        };

        activityEvents.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

        return () => {
            clearAllTimers();
            activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    return { showWarning, secondsLeft, stayLoggedIn };
};

export default useSessionTimeout;
